import {
  Box,
  Button,
  debounce,
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import Dropdown from "./basicDropdown/Dropdown";
import { roleRenamer } from "../../../helpers/RoleRenamer";
import { DELEGATED_SUB_ADMIN_LABEL } from "../../../helpers/rbacExternalAdmin";
import addAdminWhite from "../../../Photos/Newuiphotos/Admins/addAdminWhite.png";
import styles from "./styles.module.css";
import toast from "react-hot-toast";
import { useQuery } from "@apollo/client";
import { GET_MASJIDS } from "../../../graphql/queries";
import { Masjid, tempAdmin } from "../../../../Types";
import SearchDropdown from "../SearchDropdown/SearchDropdown";
import { useSearchMasjids } from "../../../graphql/Masjids/query";
import { OwnerType } from "../../../Redux/MasjidTypes";
import { isValidAdminName } from "../../../helpers/nameValidation";

type InternalAdminFormProps = {
  open: boolean;
  onClose: () => void;
  handleSubmit: (
    name: string,
    email: string,
    contact: string,
    role: string,
    selectedMasjid: string,
    isEdit?: boolean
  ) => void;
  isEditMode?: boolean;
  existingDetails?: tempAdmin | null;
  adminType: string;
  ownershipType?: string;
  /** Sub-Admin (delegated RBAC): show label only; keep real role for submit. */
  delegatedSubAdmin?: boolean;
};

const InternalAdminForm = ({
  open,
  onClose,
  handleSubmit,
  isEditMode = false,
  existingDetails = null,
  adminType,
  ownershipType,
  delegatedSubAdmin = false,
}: InternalAdminFormProps) => {
  const [masjidsList, setMasjidsList] = useState<Masjid[]>([]);
  // const [masjids, setMasjids] = useState<Masjid[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedMasjid, setSelectedMasjid] = useState("");

  const [selectedRole, setSelectedRole] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [contact, setContact] = useState("");
  const [ownerType, setOwnerType] = useState<OwnerType>(OwnerType.MASJID);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<{
    name: boolean;
    email: boolean;
    contact: boolean;
    selectedMasjid?: boolean;
    selectedRole: boolean;
    ownerType: boolean;
  }>({
    name: false,
    email: false,
    contact: false,
    selectedMasjid: false,
    selectedRole: false,
    ownerType: false,
  });
  const { masjids, loading, error } = useSearchMasjids(searchQuery);

  // Check if admin is associated with organization or individual (these hide role/masjid fields)
  const isOrganizationOrIndividual =
    ownershipType === OwnerType.ORGANIZATION ||
    ownershipType === OwnerType.INDIVIDUAL;

  // Check if admin is associated with organization, individual, or masjid (these show name/contact fields in edit)
  const isOrganizationOrIndividualOrMasjid =
    ownershipType === OwnerType.ORGANIZATION ||
    ownershipType === OwnerType.INDIVIDUAL ||
    ownershipType === OwnerType.MASJID;

  // Update masjidList when data is fetched
  useEffect(() => {
    if (masjids && !loading && !error) {
      setMasjidsList(masjids); // Update the list with fetched masjids
    }
  }, [masjids]);

  const debouncedSearchMasjids = useCallback(
    debounce((query) => {
      setSearchQuery(query);
    }, 300),
    [searchQuery]
  );
  const handleSearchMasjid = (query: string) => {
    debouncedSearchMasjids(query);
  };

  // };

  useEffect(() => {
    if (existingDetails && Object.keys(existingDetails).length > 0) {
      setSelectedRole(existingDetails?.role ?? "");
      setName(existingDetails?.name ?? "");
      setEmail(existingDetails?.email ?? "");
      setContact(existingDetails?.contact ?? "");
      setSelectedMasjid(existingDetails?.selectedMasjid ?? "");
    }
  }, [existingDetails]);

  useEffect(() => {
    if (!open) {
      setName("");
      setEmail("");
      setSelectedMasjid("");
      setSelectedRole("");
      setOwnerType(OwnerType.MASJID);
      setErrors({
        name: false,
        email: false,
        contact: false,
        selectedMasjid: false,
        selectedRole: false,
        ownerType: false,
      });
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={() => {
        setIsSubmitting(false);
        setName("");
        setEmail("");
        setSelectedMasjid("");
        setSelectedRole("");
        setErrors({
          name: false,
          email: false,
          contact: false,
          selectedMasjid: false,
          selectedRole: false,
          ownerType: false,
        });
        setIsSubmitting(false);
        setSearchQuery("");
        onClose();
      }}
      //   sx={{
      //     "& .MuiPaper-root": {},
      //   }}
      PaperProps={{
        sx: {
          width: "60%",

          borderRadius: "22px",
        },
      }}
    >
      <DialogTitle
        sx={{
          textAlign: "center",
          borderBottom: "1px solid #ccc",
          color: "#3D5347",
          fontSize: "16px",
          fontWeight: "600",
        }}
      >
        {isEditMode
          ? isOrganizationOrIndividualOrMasjid
            ? "Update Admin Details"
            : "Update Admin Details"
          : "Add New Admin Details"}
      </DialogTitle>
      <DialogContent
        sx={{
          minHeight: "400px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
        <Box>
          {/* Only show role field if not organization/individual admin */}
          {!isOrganizationOrIndividual && (
            <>
              <Typography variant="subtitle2" sx={{ marginTop: "12px", mb: 1 }}>
                Role
              </Typography>
              {delegatedSubAdmin ? (
                <Typography variant="body1" sx={{ color: "#1B8368", fontWeight: 600 }}>
                  {DELEGATED_SUB_ADMIN_LABEL}
                </Typography>
              ) : (
                <Dropdown
                  label={roleRenamer(selectedRole) || "Select a Role"}
                  options={
                    adminType === "internal"
                      ? [
                          { label: "Select a Role", value: "" },

                          {
                            label: "Data Entry",
                            value: "admin",
                          },
                        ]
                      : [
                          { label: "Select a Role", value: "" },

                          {
                            label: "Masjid Admin",
                            value: "subadmin",
                          },
                          {
                            label: "Musali Admin",
                            value: "musaliadmin",
                          },
                        ]
                  }
                  onSelect={(role: string) => {
                    setSelectedRole(role);
                    if (role === "admin" || role === "") {
                      setSelectedMasjid("");
                    }
                    if (!isSubmitting) return;
                    if (role) {
                      setErrors((oldErrors) => ({
                        ...oldErrors,
                        selectedRole: false,
                      }));
                    } else {
                      setErrors((oldErrors) => ({
                        ...oldErrors,
                        selectedRole: true,
                      }));
                    }
                  }}
                  error={errors.selectedRole}
                  selectedValue={selectedRole}
                  isMasjidChanged={false}
                ></Dropdown>
              )}
            </>
          )}

          {/* Only show masjid assignment if not organization/individual admin */}
          {!isOrganizationOrIndividual &&
            !isEditMode &&
            selectedRole !== "" &&
            selectedRole !== "admin" &&
            selectedRole !== "superadmin" && (
              <>
                <Typography
                  variant="subtitle2"
                  sx={{ marginTop: "12px", mb: 1 }}
                >
                  Assign Masjid
                </Typography>
                {masjidsList && (
                  <SearchDropdown
                    options={masjidsList || []}
                    placeholder="Type To Search Masjid."
                    onInputChange={(query) => {
                      if (query === "") {
                        setSelectedMasjid("");
                      }

                      handleSearchMasjid(query);

                      // handleSearchMasjid?.(query);
                    }} // Call the search function
                    onSelect={(selectedOption: any) => {
                      console.log(selectedOption);
                      if (selectedOption) {
                        setSelectedMasjid(selectedOption._id);
                      }
                    }}
                    error={false}
                    selectedValue={selectedMasjid}
                    loading={loading}
                    clearData={() => {
                      setSelectedMasjid("");
                    }}
                    initialValue={existingDetails?.masjidDetails?.[0]}
                  />
                )}
              </>
            )}

          {/* Only show masjid assignment in edit mode if not organization/individual admin */}
          {!isOrganizationOrIndividual &&
            isEditMode &&
            selectedRole !== "admin" &&
            selectedRole !== "superadmin" && (
              <>
                <Typography
                  variant="subtitle2"
                  sx={{ marginTop: "12px", mb: 1 }}
                >
                  Assign Masjid
                </Typography>
                {masjidsList && (
                  <SearchDropdown
                    options={masjidsList || []}
                    placeholder="Type To Search Masjid."
                    onInputChange={(query) => {
                      if (query === "") {
                        setSelectedMasjid("");
                      }
                      handleSearchMasjid(query);
                    }}
                    onSelect={(selectedOption: any) => {
                      if (selectedOption) {
                        setSelectedMasjid(selectedOption._id);
                      }
                    }}
                    error={false}
                    selectedValue={selectedMasjid}
                    loading={loading}
                    clearData={() => {
                      setSelectedMasjid("");
                    }}
                    initialValue={existingDetails?.masjidDetails?.[0]}
                  />
                )}
              </>
            )}

          {/* Only show name and email fields if not in edit mode */}
          <>
            {!isEditMode && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  marginTop: "12px",
                }}
              >
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Name*
                </Typography>
                <input
                  type="text"
                  name="fullName"
                  placeholder={"Name*"}
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    if (!isSubmitting) return;
                    if (e.target.value && isValidAdminName(e.target.value)) {
                      setErrors((oldErrors) => ({ ...oldErrors, name: false }));
                    } else {
                      setErrors((oldErrors) => ({ ...oldErrors, name: true }));
                    }
                  }}
                  style={{
                    padding: "0 15px",
                    border: "1px solid #ccc",
                    borderRadius: "25px",
                    height: "45px",
                  }}
                  className={`${errors.name ? styles.inputError : ""}`}
                />
              </Box>
            )}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                marginTop: "12px",
              }}
            >
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Email Address*
              </Typography>
              <input
                type="text"
                name="email"
                placeholder={"Email Address*"}
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (!isSubmitting) return;
                  const re = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
                  if (e.target.value && re.test(e.target.value)) {
                    setErrors((oldErrors) => ({
                      ...oldErrors,
                      email: false,
                    }));
                  } else {
                    setErrors((oldErrors) => ({ ...oldErrors, email: true }));
                  }
                }}
                style={{
                  padding: "0 15px",
                  border: "1px solid #ccc",
                  borderRadius: "25px",
                  height: "45px",
                }}
                className={`${errors.email ? styles.inputError : ""}`}
              />
            </Box>
            
            {/* Show name and contact fields for organization/individual/masjid admins in edit mode */}
            {isEditMode && isOrganizationOrIndividualOrMasjid && (
              <>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    marginTop: "12px",
                  }}
                >
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Full Name*
                  </Typography>
                  <input
                    type="text"
                    name="fullName"
                    placeholder={"Full Name*"}
                    value={name}
                    onChange={(e) => {
                      setName(e.target.value);
                      if (!isSubmitting) return;
                      if (e.target.value && isValidAdminName(e.target.value)) {
                        setErrors((oldErrors) => ({ ...oldErrors, name: false }));
                      } else {
                        setErrors((oldErrors) => ({ ...oldErrors, name: true }));
                      }
                    }}
                    style={{
                      padding: "0 15px",
                      border: "1px solid #ccc",
                      borderRadius: "25px",
                      height: "45px",
                    }}
                    className={`${errors.name ? styles.inputError : ""}`}
                  />
                </Box>
                
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    marginTop: "12px",
                  }}
                >
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Contact Number*
                  </Typography>
                  <input
                    type="text"
                    name="contact"
                    placeholder={"Contact Number*"}
                    value={contact}
                    onChange={(e) => {
                      setContact(e.target.value);
                      if (!isSubmitting) return;
                      if (e.target.value && /^[0-9+\-() ]+$/.test(e.target.value)) {
                        setErrors((oldErrors) => ({ ...oldErrors, contact: false }));
                      } else {
                        setErrors((oldErrors) => ({ ...oldErrors, contact: true }));
                      }
                    }}
                    style={{
                      padding: "0 15px",
                      border: "1px solid #ccc",
                      borderRadius: "25px",
                      height: "45px",
                    }}
                    className={`${errors.contact ? styles.inputError : ""}`}
                  />
                </Box>
              </>
            )}
          </>
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            margin: "25px",
            gap: "20px",
          }}
        >
          <Button
            variant="contained"
            sx={{
              width: "50%",
              borderRadius: "20px",
              bgcolor: "#A9A9A9",
              "&:hover": { bgcolor: "#A9A9A9" },
              textTransform: "none",
              padding: "5px",
            }}
            onClick={() => {
              setIsSubmitting(false);
              setErrors({
                name: false,
                email: false,
                contact: false,
                selectedMasjid: false,
                selectedRole: false,
                ownerType: false,
              });
              onClose();
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            sx={{
              width: "50%",
              borderRadius: "20px",
              textTransform: "none",
              padding: "5px",
              bgcolor: "#1B8368",
              ":hover": {
                borderWidth: "2px",
                bgcolor: "#1B8368",
              },
            }}
            onClick={() => {
              toast.dismiss();
              setIsSubmitting(true);
              const re = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

              // For organization/individual/masjid admins, validate email, name, and contact
              const newErrors = isOrganizationOrIndividualOrMasjid
                ? {
                    name: isEditMode && (!name || name.trim() === "") ? true : false,
                    email: !email || !re.test(email) ? true : false,
                    contact: isEditMode && (!contact || contact.trim() === "") ? true : false,
                    selectedRole: isOrganizationOrIndividual ? false : !selectedRole ? true : false, // Show role validation for masjid but not org/individual
                    ownerType: false,
                  }
                : {
                    name:
                      !isEditMode && !isValidAdminName(name) ? true : false,
                    email: !email || !re.test(email) ? true : false,
                    contact: false, // Not shown for non-organization/individual/masjid
                    selectedRole: !selectedRole ? true : false,
                    ownerType: false,
                  };

              setErrors(newErrors);

              if (!Object.values(newErrors).includes(true)) {
                handleSubmit(
                  name,
                  email,
                  contact,
                  selectedRole,
                  selectedMasjid,
                  isEditMode
                );
              } else {
                if (isEditMode) {
                  if (!re.test(email)) {
                    toast.error("Incorrect email format");
                  } else {
                    toast.error("Please fill in all the required details");
                  }
                } else {
                  if (!isValidAdminName(name)) {
                    toast.error(
                      "Incorrect name format. Must not contain digits and special characters"
                    );
                  } else if (!re.test(email)) {
                    toast.error("Incorrect email format");
                  } else {
                    toast.error("Please fill in all the required details");
                  }
                }
              }
            }}
          >
            <Box
              component="img"
              src={addAdminWhite}
              sx={{ width: "20px", marginRight: "8px" }}
            />
            {isEditMode
              ? isOrganizationOrIndividualOrMasjid
                ? "Update Admin"
                : "Update Admin"
              : "Add new Admin"}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default InternalAdminForm;
